import { customerService, type Customer } from "@api/customer";
import { buildContactsExportCsv, downloadCsvFile } from "@lib/csv-export";
import { formatDate } from "@lib/utils";

/**
 * Integration sync namespaces under `properties.*` hold large nested blobs
 * (full Mariana Tek / Mindbody / etc. profiles). Returning them for hundreds of
 * contacts at once pushes a single list response past the Lambda Function URL
 * 6MB cap, which surfaces as a 502. We exclude them here so the export keeps the
 * human-meaningful data (core columns + root-level custom fields + form values)
 * without the bloat.
 *
 * This is a stopgap for the synchronous client-side export; a backend async
 * export job is the long-term fix for unbounded org sizes.
 */
const EXCLUDED_PROPERTY_NAMESPACES = [
  "mindbody",
  "mariana_tek",
  "fitdegree",
  "tablelist",
  "clubready",
  "gymble",
  "hubspot",
  "shopify",
  "square",
];

// Pure-exclusion projection: returns all customer fields except the heavy
// integration namespaces. Mongo does not allow mixing field inclusion with
// nested-path exclusion, so this must stay exclusion-only.
const EXPORT_SELECT = EXCLUDED_PROPERTY_NAMESPACES.map((ns) => `-properties.${ns}`).join(" ");

const PAGE_SIZE = 500;

export async function fetchAllCustomersForExport(gym: string): Promise<Customer[]> {
  const customers: Customer[] = [];
  let offset = 0;
  let total = Number.POSITIVE_INFINITY;

  while (customers.length < total) {
    const { data, headers } = await customerService.listWithHeaders({
      gym,
      limit: PAGE_SIZE,
      offset,
      sort: "-createdAt",
      select: EXPORT_SELECT,
    });
    total = Number(headers.get("x-helios-meta-count") ?? data.length);
    customers.push(...data);
    if (data.length < PAGE_SIZE) break;
    offset += PAGE_SIZE;
  }

  return customers;
}

export async function exportContactsToCsv(gym: string): Promise<number> {
  const customers = await fetchAllCustomersForExport(gym);
  const csv = buildContactsExportCsv(customers);
  const filename = `contacts-export-${formatDate(new Date(), "yyyy-MM-dd")}.csv`;
  downloadCsvFile(csv, filename);
  return customers.length;
}
